import { useVideoCached } from '@/features/shared/Hooks/useVideoCached';
import { isAutoplayVideoAllowed } from '@/utils/helpers';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useEffect, useRef, useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import YouTube, { Options as YoutubeOptions } from 'react-youtube';
import Image from '../shared/Image';
import { VideoProps } from './Video.interface';
import useStyles from './Video.styles';
import { isNoscriptVideoBlocked } from './Video.utils';

interface EmbedPlayerProps {
  classes: any;
  videoUrl: string;
  // eslint-disable-next-line react/no-unused-prop-types
  onVideoPlayed?: any;
}

const CustomVideo = memo(
  ({ classes, videoUrl, onVideoPlayed = () => {} }: EmbedPlayerProps): JSX.Element => {
    const video = useRef<HTMLVideoElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const onChange = (visible: boolean) => {
      setIsVisible(visible);
    };

    const playVideo = () => {
      const playPromise = video.current?.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('video played auto');
            onVideoPlayed();
          })
          .catch(() => {
            console.log('playback prevented');
          });
      }
    };

    const pauseVideo = () => {
      video.current?.pause();
      console.log('video paused');
    };

    const loadVideo = () => {
      const source = video.current?.firstChild as HTMLSourceElement;

      if (source?.hasAttribute('data-src')) {
        if (isVisible) {
          if (source) {
            source.src = source?.getAttribute('data-src') || '';
            source?.removeAttribute('data-src');
            video.current?.load();
          }
        }

        return true;
      }

      return false;
    };

    useEffect(() => {
      (async () => {
        const loading = await loadVideo();

        if (loading) return;

        if (isVisible) {
          playVideo();
        } else {
          pauseVideo();
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    useEffect(() => {
      if (isVisible) {
        loadVideo();
      } else {
        pauseVideo();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoUrl]);

    return (
      <VisibilitySensor onChange={onChange} delayedCall scrollCheck intervalCheck>
        <video
          ref={video}
          autoPlay={isAutoplayVideoAllowed}
          muted
          loop
          playsInline
          className={classes.customVideo}
          tabIndex={-1}
        >
          <source data-src={videoUrl} />
        </video>
      </VisibilitySensor>
    );
  },
);

const YoutubeVideo = memo(({ classes, videoUrl }: EmbedPlayerProps) => {
  const [videoRef, setVideoRef] = useState<any>(null);

  const getYotubeId = () => {
    if (videoUrl.length === 11) return videoUrl;
    // eslint-disable-next-line no-useless-escape
    const REGEX_GET_YOUTUBE_ID =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    const MATCH = videoUrl.match(REGEX_GET_YOUTUBE_ID);
    return MATCH && MATCH[7].length === 11 ? MATCH[7] : '';
  };
  const restartVideo = (event: any) => {
    event.target.seekTo(0);
    event.target.stopVideo();
  };

  const videoOpts: YoutubeOptions = {
    playerVars: {
      rel: 0,
      modestbranding: 1,
      iv_load_policy: 3,
    },
  };

  const onChange = (visible: boolean) => {
    if (!visible && !!videoRef) {
      videoRef?.pauseVideo();
    }
  };

  return videoUrl ? (
    <VisibilitySensor onChange={onChange} delayedCall scrollCheck intervalCheck>
      <YouTube
        data-testid="qa-video-component-iframe"
        className={classes.youtubeVideo}
        videoId={getYotubeId()}
        opts={videoOpts}
        onReady={(event) => setVideoRef(event.target)}
        onEnd={restartVideo}
      />
    </VisibilitySensor>
  ) : (
    <div>Youtube ID is not valid</div>
  );
});

const VideoComponent = ({ videoUrl = '', fallbackImage = {} }: PropsWithChildren<VideoProps>) => {
  const classes = useStyles({});
  const [isVideoPlayed, setIsVideoPlayed] = React.useState<boolean>(false);
  const [isVideoBlocked, setIsVideoBlocked] = React.useState<boolean>(true);

  const isCustomVideo = React.useMemo(() => {
    const IS_YOUTUBE = /youtu/i.test(videoUrl);
    const IS_YOUTUBE_ID = videoUrl.length === 11;
    const IS_CUSTOM_VIDEO = !IS_YOUTUBE && !IS_YOUTUBE_ID;
    return IS_CUSTOM_VIDEO;
  }, [videoUrl]);

  const VideoVariant = React.useMemo(
    () => (isCustomVideo ? CustomVideo : YoutubeVideo),
    [isCustomVideo],
  );

  const videoClassName = clsx(classes.video, isCustomVideo && 'is-custom');
  const [isVideoCached] = useVideoCached({ videoUrl, isCustomVideo, isVideoPlayed });
  const isFallbackShown = React.useMemo(
    () => !!fallbackImage && isCustomVideo && (!isVideoCached || isVideoBlocked),
    [fallbackImage, isVideoCached, isCustomVideo, isVideoBlocked],
  );
  const onVideoPlayed = React.useCallback(() => setIsVideoPlayed(true), [setIsVideoPlayed]);
  React.useEffect(() => {
    let shouldUpdate = true;

    if (!isCustomVideo) {
      setIsVideoBlocked(false);
      return;
    }

    isNoscriptVideoBlocked(videoUrl).then(
      (response) => shouldUpdate && setIsVideoBlocked(response),
    );

    return () => {
      shouldUpdate = false;
    };
  }, [isCustomVideo, videoUrl]);

  return (
    <section data-testid="qa-video-component" className={videoClassName}>
      {isFallbackShown && (
        <Image
          image={fallbackImage}
          base="small"
          type="background"
          size="cover"
          roundedCorners={false}
          className={classes.fallbackImage}
        />
      )}
      {!isVideoBlocked && (
        <VideoVariant classes={classes} videoUrl={videoUrl} onVideoPlayed={onVideoPlayed} />
      )}
    </section>
  );
};

export default memo(VideoComponent);
